import { expect } from '@playwright/test';
import { test as base } from '@stencil/playwright';
import AxeBuilder from '@axe-core/playwright';
import { type SerialFrameSelector } from 'axe-core';

type AxeFixture = {
  elementsAxeBuilder: () => AxeBuilder;
};

// Extend Playwright "test" function with a consistently configured
// AxeBuilder instance with some rules disabled by default.
export const test = base.extend<AxeFixture>({
  elementsAxeBuilder: async ({ page }, use) => {
    // Create a function that returns a fresh AxeBuilder instance using the current page state
    const elementsAxeBuilder = () =>
      new AxeBuilder({ page })
        // These rules are disabled because they are relevant to pages, not components
        // https://dequeuniversity.com/rules/axe/4.7/region
        // https://dequeuniversity.com/rules/axe/4.7/landmark-one-main
        .disableRules(['region', 'landmark-one-main']);

    await use(elementsAxeBuilder);
  },
});
export { expect } from '@playwright/test';

export type AxeTestOptions = {
  /**
   * Optional name for the test, will default to 'reports no accessibility violations'
   */
  testName?: string;
  /**
   * Optional markup to render. If not provided, the test will use the content already set in the page.
   */
  markup?: string;
  /**
   * Optional selector to test. Useful when testing specific variations with different selectors.
   */
  selector?: SerialFrameSelector;
};

// Use axeTest to catch about half of a11y issues with just a few lines of code:
// https://www.deque.com/blog/automated-testing-study-identifies-57-percent-of-digital-accessibility-issues/
// Manual a11y testing is still required for each component.
export const axeTest = (options?: AxeTestOptions) => {
  const testName = options?.testName || 'reports no accessibility violations';

  test(testName, async ({ page, elementsAxeBuilder }) => {
    // If markup is provided, use that
    if (options?.markup) {
      await page.setContent(options.markup);
    }
    // Otherwise we assume the page content is already set up by beforeEach or other means

    // Determine which selector to use for the axe test
    // If no selector is provided, use the whole page
    const selector = options?.selector || 'body';

    // Perform accessibility testing using axe-core
    // The elementsAxeBuilder already has the current page from the fixture
    const accessibilityScanResults = await elementsAxeBuilder().include(selector).analyze();

    expect(accessibilityScanResults.violations).toEqual([]);
  });
};
